import { FC, HTMLAttributes, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipProps } from 'recharts-overriden';

import { BenchmarkType, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { Bar, BarChart, Cell, Chart } from '@/components/Charts/components/Chart';
import { Dot } from '@/components/Charts/components/Dot';
import {
  ChartTooltip,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from '@/components/Charts/components/Tooltip';
import { Logger } from '@/lib/logs/logger';
import { colorPalette } from '@/theme/colorPalette';

import { NoChartData } from './NoChartData';
import { GraphFact } from './types';

const BENCHMARK_ORDER: BenchmarkType[] = [
  BenchmarkType.very_low,
  BenchmarkType.low,
  BenchmarkType.moderate,
  BenchmarkType.high,
  BenchmarkType.very_high,
];

const BENCHMARK_COLORS = {
  [BenchmarkType.very_low]: '#b8d4e8',
  [BenchmarkType.low]: '#8eb5d6',
  [BenchmarkType.moderate]: '#6698be',
  [BenchmarkType.high]: '#3a79b6',
  [BenchmarkType.very_high]: '#1a4b7f',
};

const RECTANGLE_RADIUS = 4 as const;

type HabitatConnectivityChartProps = {
  data: GraphFact[];
  onBarClick?: (item: ChartDataItem) => void;
};

export type ChartDataItem = {
  label: string;
  count: number;
  benchmark: BenchmarkType;
  plots: {
    id: string;
    name: string;
    value: number;
  }[];
};

export const HabitatConnectivityChart: FC<HTMLAttributes<HTMLDivElement> & HabitatConnectivityChartProps> = ({
  data,
  onBarClick,
  ...delegated
}) => {
  const { t } = useTranslation();

  const chartData = useGetChartDataFromGraphFacts(data);

  if (chartData.length === 0) {
    Logger.error('Not enough data to render HabitatConnectivityChart');
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const maxCount = Math.max(...chartData.map((d) => d.count));

  return (
    <Chart.Container
      {...delegated}
      data={chartData}
      config={{
        count: {
          label: t('shared.ncaDetail.details.habitatConnectivity.labels.plotsCount'),
          color: BENCHMARK_COLORS[BenchmarkType.high],
        },
      }}
    >
      <BarChart data={chartData}>
        <Bar
          isAnimationActive={false}
          dataKey='count'
          radius={[RECTANGLE_RADIUS, RECTANGLE_RADIUS, 0, 0]}
          activeBar={(props: unknown) => (
            <CustomActiveBar {...(props as ActiveBarProps)} style={{ cursor: onBarClick ? 'pointer' : 'default' }} />
          )}
          onClick={({ payload }: { payload: ChartDataItem }) =>
            payload.plots.length > 0 ? onBarClick?.(payload) : undefined
          }
        >
          {chartData.map((entry) => (
            <Cell key={entry.label} fill={BENCHMARK_COLORS[entry.benchmark as keyof typeof BENCHMARK_COLORS]} />
          ))}
        </Bar>
        <Chart.XAxis dataKey='label' type='category' />
        <Chart.YAxis
          domain={[0, maxCount]}
          ticks={Array.from({ length: maxCount + 1 }, (_, i) => i)}
          allowDecimals={false}
        >
          <Chart.Label>{t('shared.ncaDetail.details.habitatConnectivity.labels.plotsCount')}</Chart.Label>
        </Chart.YAxis>
        <Chart.Tooltip content={(props) => <CustomTooltip {...props} />} cursor={false} />
      </BarChart>
    </Chart.Container>
  );
};

type ActiveBarProps = SVGProps<SVGElement> & { background: { y: number }; fill: string };

const CustomActiveBar = ({ x, y, width, height, background, fill, style }: ActiveBarProps) => {
  if (!x || !y || !width || !height) return <></>;

  const barMiddleX = (x as number) + (width as number) / 2;
  const barBottom = (y as number) + (height as number);
  const barTop = background.y;

  const dotProps = {
    cx: barMiddleX,
    cy: y as number,
    fill: '#000000',
    strokeWidth: 0,
    opacity: 1,
  };

  return (
    <g style={style}>
      <rect x={x} y={y} width={width} height={height} fill={`${fill}99`} ry={RECTANGLE_RADIUS} />
      <line x1={barMiddleX} x2={barMiddleX} y1={barTop} y2={barBottom} stroke={colorPalette.divider} strokeWidth={1} />
      <Dot.Container {...dotProps}>
        <Dot.Active {...dotProps} fill='#000000' />
      </Dot.Container>
    </g>
  );
};

const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const item = payload[0]?.payload as ChartDataItem | undefined;

  if (!item) return null;

  return (
    <ChartTooltip>
      <ChartTooltipTextPrimary>{item.label}</ChartTooltipTextPrimary>
      <ChartTooltipTextSecondary>
        {`${t('shared.ncaDetail.details.labels.numberOfPlots')}: ${t('shared.ncaDetail.details.labels.plotWithCount', { count: item.count })}`}
      </ChartTooltipTextSecondary>
    </ChartTooltip>
  );
};

const useGetChartDataFromGraphFacts = (graphFacts: GraphFact[]): ChartDataItem[] => {
  const { t } = useTranslation();

  let prevValue = 0;

  return BENCHMARK_ORDER.flatMap((benchmark) => {
    const fact = graphFacts.find((f) =>
      f.labels?.some((l) => l.name === PlotReportFactLabelNameEnum.benchmark && l.value === benchmark),
    );

    if (!fact) {
      return [];
    }

    const displayValue = Math.round(fact.value * 100);
    const unit = t('global.ui.unit.%');
    const label = `${prevValue} - ${displayValue}${unit}`;
    const plotValues = fact.plot_values ?? [];

    prevValue = displayValue;

    return {
      label,
      count: plotValues.length,
      benchmark,
      plots: plotValues,
    };
  });
};
